import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/SimpleToast";
import { Loader2, KeyRound, Eye, EyeOff, CheckCircle2, XCircle } from "lucide-react";
import { inviteApi } from "@/lib/api";

export default function InviteAccept() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [status, setStatus] = useState("checking"); // checking | valid | invalid | done
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setStatus("invalid");
      return;
    }
    inviteApi
      .validate(token)
      .then((res) => {
        if (!active) return;
        setInvite(res.data);
        setStatus("valid");
      })
      .catch(() => active && setStatus("invalid"));
    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Das Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    if (password !== confirm) {
      toast.error("Die Passwörter stimmen nicht überein");
      return;
    }
    setSaving(true);
    try {
      await inviteApi.accept(token, password);
      setStatus("done");
      toast.success("Passwort gesetzt. Sie können sich jetzt anmelden.");
      setTimeout(() => navigate("/login"), 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Passwort konnte nicht gesetzt werden");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.8), rgba(15, 23, 42, 0.95)), url('https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxzZXJ2ZXIlMjByb29tJTIwYWJzdHJhY3QlMjBibHVlfGVufDB8fHx8MTc3MDA1NTkwM3ww&ixlib=rb-4.1.0&q=85')`,
      }}
      data-testid="invite-accept-page"
    >
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-8">
          <img
            src="/assets/rnetwork-logo.webp"
            alt="R-Network GmbH"
            className="h-16 mx-auto mb-4 bg-white rounded-lg px-4 py-2"
          />
          <h1 className="text-3xl font-bold text-white">Willkommen</h1>
          <p className="text-slate-400 mt-2">Konto einrichten</p>
        </div>

        <Card className="border-border/50 shadow-2xl">
          {status === "checking" && (
            <CardContent className="py-12 flex flex-col items-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-muted-foreground text-sm">Einladung wird geprüft…</p>
            </CardContent>
          )}

          {status === "invalid" && (
            <CardContent className="py-12 flex flex-col items-center gap-3 text-center" data-testid="invite-invalid">
              <XCircle className="w-12 h-12 text-destructive" />
              <h2 className="text-lg font-semibold">Einladung ungültig</h2>
              <p className="text-muted-foreground text-sm">
                Dieser Einladungslink ist ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.
              </p>
              <Button variant="outline" onClick={() => navigate("/login")} data-testid="invite-to-login-btn">
                Zur Anmeldung
              </Button>
            </CardContent>
          )}

          {status === "done" && (
            <CardContent className="py-12 flex flex-col items-center gap-3 text-center" data-testid="invite-done">
              <CheckCircle2 className="w-12 h-12 text-success" />
              <h2 className="text-lg font-semibold">Passwort gesetzt</h2>
              <p className="text-muted-foreground text-sm">Sie werden zur Anmeldung weitergeleitet…</p>
            </CardContent>
          )}

          {status === "valid" && (
            <>
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-xl flex items-center justify-center gap-2">
                  <KeyRound className="w-5 h-5 text-primary" />
                  Passwort festlegen
                </CardTitle>
                <CardDescription>
                  Hallo {invite?.full_name}, legen Sie ein sicheres Passwort für{" "}
                  <span className="font-medium">{invite?.email}</span> fest.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-password">Neues Passwort</Label>
                    <div className="relative">
                      <Input
                        id="invite-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Mind. 8 Zeichen"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="invite-password-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-confirm">Passwort bestätigen</Label>
                    <Input
                      id="invite-confirm"
                      type={showPassword ? "text" : "password"}
                      placeholder="Passwort wiederholen"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      data-testid="invite-confirm-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-11"
                    disabled={saving}
                    data-testid="invite-submit-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Passwort speichern & aktivieren"}
                  </Button>
                </form>
              </CardContent>
            </>
          )}
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          © 2026 R-Network GmbH - Nur für internen Gebrauch
        </p>
      </div>
    </div>
  );
}
